import { AttachmentFile } from "@inrev/common";
import { useMemo, useState } from "react";
import { type AttachmentContext, trackAttachmentAction } from "../../analytics";
import { cn } from "../lib/utils";
import { type PDFViewerControl } from "../patterns/pdf-viewer";
import { CustomFileIcon } from "../ui/CustomFileIcon";
import { PDFViewerModal } from "../ui/PDFViewerModal";
import { TruncatedLabel } from "../ui/TruncatedLabel";
import { trackingAttrs } from "../utils";

type UploadedDocumentNameWithIconProps = {
	row: AttachmentFile;
	actions: {
		downloadBaseUrl: string;
		downloadQueryKeyPrefix: string;
		analyticsContext: AttachmentContext;
	};
};

export const UploadedDocumentNameWithIcon = ({
	row,
	actions,
}: UploadedDocumentNameWithIconProps) => {
	const [pdfOpen, setPdfOpen] = useState(false);
	const extension = row.file.extension;
	const name = [row.file.name, extension].join(".");
	const isPdf = extension === "pdf";
	const attachmentId = row.id;
	const downloadBaseUrl = actions.downloadBaseUrl;
	const downloadQueryKeyPrefix = actions.downloadQueryKeyPrefix;
	const analyticsContext = actions.analyticsContext;
	const control = useMemo<PDFViewerControl>(
		() => ({
			fetchUrl: `${downloadBaseUrl}/${attachmentId}`,
			queryKey: [downloadQueryKeyPrefix, attachmentId, true],
			fileId: row.file.id,
		}),
		[downloadBaseUrl, downloadQueryKeyPrefix, attachmentId, row.file.id],
	);
	return (
		<div
			{...trackingAttrs(isPdf ? `${analyticsContext}-attachment-name-preview` : undefined)}
			className={cn("flex items-center gap-3", isPdf ? "cursor-pointer" : "")}
			onClick={
				isPdf
					? () => {
							trackAttachmentAction("preview", analyticsContext);
							setPdfOpen(true);
						}
					: undefined
			}
		>
			<CustomFileIcon extension={extension} />
			<TruncatedLabel mask label={name} className="text-xs xl:text-sm font-medium text-primary" />
			{pdfOpen && isPdf && (
				<PDFViewerModal
					onClose={() => setPdfOpen(false)}
					control={control}
					header={row.file.name}
					trackingId={`${analyticsContext}-attachment-viewer`}
				/>
			)}
		</div>
	);
};
